<template>
  <div v-if="schedule" class="schedule-container">
    <div>
      <a @click="lookupSchedule">
        {{ teacher.firstName }} {{ teacher.lastName }}
      </a>
      <span v-if="!globalCenter || !globalCenter.id">
        @ {{ getCenterName(schedule.centerId) }}
      </span>
    </div>

    <div>
      {{ schedule.startTime | formatDate }},
      {{ schedule.startTime | formatTime }} -
      {{ schedule.endTime | formatTime }}
    </div>

    <div v-if="schedule.partOfRecurring">
      Weekly on {{ schedule.recurringDays.join(", ") }}
    </div>
    <div v-if="schedule.partOfRecurring">
      Since {{ schedule.recurringStartDate | formatDate }}
      {{
        schedule.recurringEndDate
          ? ` until ${$options.filters.formatDate(schedule.recurringEndDate)}`
          : " (no end date)"
      }}
    </div>

    <div v-if="schedule.classroomId">
      Classroom: {{ classroomName(schedule.classroomId) }}
    </div>

    <div
      v-if="schedule && schedule2Course && schedule2Course[schedule.scheduleId]"
    >
      <a @click="viewCourse(schedule2Course[schedule.scheduleId])">
        {{ schedule2Course[schedule.scheduleId].name }}
      </a>
    </div>

    <div v-if="schedule.notes">
      <pre> {{ schedule.notes }} </pre>
    </div>

<v-divider class="my-2"/>

    <div class="d-flex justify-space-between">
      <div class="my-auto">
        <v-btn small @click="assignStudent" color="blue" text> Assign Student </v-btn>

        <add-to-google-calendar-button
          color="blue darken-1"
          text
          v-if="!googleCalEvent"
          :schedule="schedule"
          @on-success="added2GoogleCalendar"
        />
      </div>

      <span v-if="googleCalEvent && googleCalEvent.link">
        <v-btn
          color="blue darken-1"
          text
          target="_blank"
          :href="googleCalEvent.link"
        >
          <v-icon left>mdi-calendar</v-icon>
          Google Calendar
          <v-icon right>mdi-open-in-new</v-icon>
        </v-btn>
      </span>

      <div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              icon
              v-bind="attrs"
              v-on="on"
              @click="editSchedule"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Edit Schedule</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment-timezone";
import AddToGoogleCalendarButton from "@/components/schedule/AddToGoogleCalendarButton.vue";
import { openNewStudentSchedule, openTeacherScheduleLookup, openEditTeacherScheduleInstance } from "@/components/schedule/eventHandler.js";

export default {
  components: { AddToGoogleCalendarButton },
  props: {
    schedule: {
      type: Object,
      required: true,
    },
    teacher: {
      type: Object,
      default: null,
    },
    classrooms: {
      type: Array,
      default: null,
    },
  },
  data: () => ({
    schedule2Course: {},
    googleCalEvent: null,
  }),
  watch: {
    schedule: {
      handler(newVal) {
        this.schedule = newVal;
        this.initData();
      },
      deep: true,
    },
  },
  mounted() {
    this.initData();
  },
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),
    ...mapGetters("scheduleCentral", ["dateRange"]),
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    initData() {
      this.schedule2Course = null;
      this.googleCalEvent = null;

      this.fetchCourseInfo([this.schedule]);
      this.fetchGoogleCalendarEvent(this.schedule.googleCalendarEventId);
    },
    deleteSchedule(schedule) {
      this.$emit("delete-schedule", schedule);
    },
    editSchedule() {
      openEditTeacherScheduleInstance( {teacherSchedule: this.schedule } )
    },
    fetchCourseInfo(schedules) {
      if (schedules) {
        const teacherScheduleIds = schedules.map((s) => s.scheduleId);
        const params = {
          teacher_schedule_ids: teacherScheduleIds
            ? teacherScheduleIds.join(",")
            : null,
        };
        this.$axios("/teacher-schedule/courses-with-teacher-schedules", {
          params,
        }).then((resp) => {
          this.schedule2Course = resp.data;
        });
      }
    },
    fetchGoogleCalendarEvent(googleCalendarEventId) {
      if (googleCalendarEventId) {
        const start = moment(this.schedule.startTime).format("YYYY-MM-DD");
        const end = moment(this.schedule.startTime).format("YYYY-MM-DD");
        const params = {
          start_date: start,
          end_date: end,
        };

        this.$axios
          .get("/google-calendar-events/" + googleCalendarEventId, { params })
          .then((resp) => {
            const matchingEvents = resp.data;

            if (matchingEvents.length > 0) {
              const event = matchingEvents[0];
              this.googleCalEvent = {
                id: event.id,
                link: event.htmlLink,
                conferenceData: event.conferenceData,
                description: event.summary,
                start: event.start.dateTime.value,
                end: event.end.dateTime.value,
              };
            } else {
              console.log("No matching events found for the given start time.");
            }
          });
      }
    },
    assignStudent() {

      const recurring = this.schedule.partOfRecurring;
      const recurringEndDate = this.schedule.recurringEndDate;
      const recurringDays = this.schedule.recurringDays;
      const startDate = moment(this.schedule.startTime).format("YYYY-MM-DD");
      const startTime = moment(this.schedule.startTime).format("HH:mm");
      const endTime = moment(this.schedule.endTime).format("HH:mm");
      
      const data = {
          teacherId: this.teacher.id,
          teacherName: `${this.teacher.firstName} ${this.teacher.lastName}`,
          teacher: this.teacher,
          startDate,
          startTime,
          endTime,
          recurring,
          recurringEndDate,
          recurringDays,
        };

      openNewStudentSchedule(data);

    },
    added2GoogleCalendar(event) {
      this.googleCalEvent = {
        id: event.id,
        link: event.htmlLink,
        conferenceData: event.conferenceData,
        description: event.description,
      };
    },
    classroomName(classroomId) {
      if (this.classrooms) {
        return this.classrooms.find((c) => c.id == classroomId).roomNumber;
      } else {
        return null;
      }
    },
    viewCourse(course) {
      let route = this.$router.resolve({ path: "/secure/course/" + course.id });
      window.open(route.href, "_blank");
    },
    lookupSchedule() {
      openTeacherScheduleLookup({teacher: this.teacher})
    },
  },
};
</script>

<style scoped>
.schedule-container {
  border: 1px solid #ccc;
  margin-bottom: 20px;
  padding: 20px;
}
</style>
